import React from "react";
import { Helmet } from "react-helmet-async";

export const SITE = "https://johnnymart.net";
const CURRENCY = "USD";

// Mirrors backend USPS_TIERS so structured-data shipping matches checkout.
const USPS_TIERS = [
  [4, 4.63], [8, 5.10], [12, 5.86], [16, 6.55],
  [32, 8.50], [48, 10.0], [64, 11.5], [80, 13.0],
];
export function uspsEstimate(weightOz) {
  let oz = Number(weightOz) || 16;
  if (oz <= 0) oz = 1;
  for (const [maxOz, price] of USPS_TIERS) if (oz <= maxOz) return price;
  return 21.0;
}

const CONDITION_SCHEMA = {
  "New / factory sealed": "https://schema.org/NewCondition",
  "New with tags": "https://schema.org/NewCondition",
  "New without tags": "https://schema.org/NewCondition",
  "Open box": "https://schema.org/UsedCondition",
  "Like new": "https://schema.org/UsedCondition",
  "Excellent used condition": "https://schema.org/UsedCondition",
  "Good used condition": "https://schema.org/UsedCondition",
  "Fair condition": "https://schema.org/UsedCondition",
  "For parts or repair": "https://schema.org/DamagedCondition",
};

// Descriptive alt text: brand + name + color + condition.
export function productImageAlt(product) {
  return [product?.brand, product?.name, product?.color, product?.condition]
    .filter(Boolean).join(" - ");
}

export function buildProductJsonLd(product, reviewSummary, plainDesc) {
  if (!product || product.price == null) return null;
  const canonical = `${SITE}/product/${product.slug}`;
  const soldOut = product.sold_out === true
    || (product.track_inventory === true && (product.stock || 0) <= 0);
  const validUntil = new Date(Date.now() + 365 * 864e5).toISOString().slice(0, 10);
  const shipRate = uspsEstimate(product.weight_oz);

  const offer = {
    "@type": "Offer",
    url: canonical,
    priceCurrency: CURRENCY,
    price: Number(product.price).toFixed(2),
    priceValidUntil: validUntil,
    availability: soldOut ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
    itemCondition: CONDITION_SCHEMA[product.condition] || "https://schema.org/UsedCondition",
    seller: { "@type": "Organization", name: "JohnnyMart" },
    shippingDetails: {
      "@type": "OfferShippingDetails",
      shippingRate: { "@type": "MonetaryAmount", value: shipRate.toFixed(2), currency: CURRENCY },
      shippingDestination: { "@type": "DefinedRegion", addressCountry: "US" },
      deliveryTime: {
        "@type": "ShippingDeliveryTime",
        handlingTime: { "@type": "QuantitativeValue", minValue: 1, maxValue: 3, unitCode: "DAY" },
        transitTime: { "@type": "QuantitativeValue", minValue: 2, maxValue: 6, unitCode: "DAY" },
      },
    },
    hasMerchantReturnPolicy: {
      "@type": "MerchantReturnPolicy",
      applicableCountry: "US",
      returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
      merchantReturnDays: 7,
      returnMethod: "https://schema.org/ReturnByMail",
      returnFees: "https://schema.org/FreeReturn",
    },
  };

  const ld = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: plainDesc || product.name,
    image: product.images && product.images.length ? product.images : undefined,
    sku: product.sku || product.id,
    ...(product.brand ? { brand: { "@type": "Brand", name: product.brand } } : {}),
    ...(product.color ? { color: product.color } : {}),
    ...(product.gtin ? { gtin: product.gtin } : {}),
    ...(product.mpn ? { mpn: product.mpn } : {}),
    offers: offer,
  };
  if (reviewSummary && reviewSummary.count > 0 && reviewSummary.average > 0) {
    ld.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: Number(reviewSummary.average).toFixed(1),
      reviewCount: reviewSummary.count,
    };
  }
  return ld;
}

// Reusable per-page SEO: unique title, meta description, canonical, Open Graph.
export const Seo = ({ title, description, path = "", image, type = "website", noindex = false, jsonLd }) => {
  const fullTitle = title ? `${title} | JohnnyMart` : "JohnnyMart";
  const url = `${SITE}${path}`;
  const desc = (description || "").slice(0, 300);
  return (
    <Helmet>
      <title>{fullTitle}</title>
      {desc && <meta name="description" content={desc} />}
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,follow" />}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      {desc && <meta property="og:description" content={desc} />}
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content="JohnnyMart" />
      {image && <meta property="og:image" content={image} />}
      <meta name="twitter:card" content={image ? "summary_large_image" : "summary"} />
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
};
